import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  InputAdornment,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import axios from "axios";

/**
 * Register Page (Manual Program Name entry — no dropdown)
 * - Elegant glass card on animated gradient background
 * - Client-side validation; disabled submit until valid
 * - Loading + Snackbar success/error feedback
 * - "Program Name" is a normal text field users type manually
 * - Terms link -> /terms-and-conditions
 * - API base via env (fallback to localhost)
 */

const API_BASE = import.meta?.env?.VITE_API_URL || "http://localhost:5000";

export default function RegisterPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    programName: "", // manual text input
    agree: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  // Validation
  const isEmailValid = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(form.email.trim()),
    [form.email]
  );
  const isMobileValid = useMemo(
    () => /^[0-9]{10}$/.test(form.mobile.replace(/\D/g, "")),
    [form.mobile]
  );
  const isNameValid = form.name.trim().length >= 2;
  const isProgramValid = form.programName.trim().length >= 3; // simple length check for manual entry
  const isFormValid = isNameValid && isEmailValid && isMobileValid && isProgramValid && form.agree;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) {
      setSnack({
        open: true,
        severity: "error",
        message: "Please fill all fields correctly and accept the Terms & Conditions.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        programName: form.programName.trim(),
      };
      const { data } = await axios.post(`${API_BASE}/register`, payload);

      setSnack({
        open: true,
        severity: "success",
        message: data?.message || "Registered successfully!",
      });

      setTimeout(() => {
        navigate("/login", { replace: true });
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 500);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
      setSnack({ open: true, severity: "error", message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        background:
          "linear-gradient(135deg, #0f172a, #1e293b, #0b1220, #0f172a, #111827)",
        backgroundSize: "300% 300%",
        animation: "bgmove 18s ease infinite",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "min(520px, 94vw)",
          borderRadius: 4,
          p: { xs: 3, sm: 4 },
          color: "primary.contrastText",
          backdropFilter: "blur(10px)",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 12px 36px rgba(0,0,0,.35)",
        }}
      >
        <Box textAlign="center" mb={1}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              letterSpacing: ".3px",
              background:
                "linear-gradient(90deg, #06b6d4 0%, #4f46e5 40%, #1d4ed8 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Create your account
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
            Join our programs designed for growth and success.
          </Typography>
        </Box>

        <Divider sx={{ my: 2.5, borderColor: "rgba(255,255,255,0.12)" }} />

        <Box component="form" noValidate onSubmit={handleSubmit}>
          <TextField
            label="Full Name"
            name="name"
            fullWidth
            required
            margin="normal"
            value={form.name}
            onChange={handleChange}
            error={form.name.length > 0 && !isNameValid}
            helperText={form.name.length > 0 && !isNameValid ? "Enter at least 2 characters" : " "}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineRoundedIcon sx={{ color: "rgba(255,255,255,0.85)" }} />
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />

          <TextField
            label="Email"
            name="email"
            type="email"
            fullWidth
            required
            margin="normal"
            value={form.email}
            onChange={handleChange}
            error={form.email.length > 0 && !isEmailValid}
            helperText={form.email.length > 0 && !isEmailValid ? "Enter a valid email" : " "}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineRoundedIcon sx={{ color: "rgba(255,255,255,0.85)" }} />
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />

          <TextField
            label="Mobile (10 digits)"
            name="mobile"
            inputMode="numeric"
            fullWidth
            required
            margin="normal"
            value={form.mobile}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
              setForm((s) => ({ ...s, mobile: digits }));
            }}
            error={form.mobile.length > 0 && !isMobileValid}
            helperText={form.mobile.length > 0 && !isMobileValid ? "Enter a valid 10-digit number" : " "}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIphoneRoundedIcon sx={{ color: "rgba(255,255,255,0.85)" }} />
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />

          {/* Manual program name input (no dropdown) */}
          <TextField
            label="Program Name"
            name="programName"
            fullWidth
            required
            margin="normal"
            value={form.programName}
            onChange={handleChange}
            error={form.programName.length > 0 && !isProgramValid}
            helperText={
              form.programName.length > 0 && !isProgramValid
                ? "Enter at least 3 characters"
                : " "
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SchoolRoundedIcon sx={{ color: "rgba(255,255,255,0.85)" }} />
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />

          <FormControlLabel
            control={
              <Checkbox
                name="agree"
                checked={form.agree}
                onChange={handleChange}
                color="primary"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                I agree to the{" "}
                <Link
                  to="/terms-and-conditions"
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  style={{ color: "#93c5fd", textDecoration: "underline" }}
                >
                  Terms &amp; Conditions
                </Link>
              </Typography>
            }
            sx={{ mt: 0.5 }}
          />

          <Button
            type="submit"
            fullWidth
            size="large"
            disabled={!isFormValid || submitting}
            sx={{
              mt: 2,
              py: 1.4,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 800,
              letterSpacing: ".3px",
              color: "#fff",
              background:
                "linear-gradient(90deg, #06b6d4 0%, #4f46e5 50%, #1d4ed8 100%)",
              boxShadow: "0 10px 22px rgba(17,24,39,0.55)",
              "&:hover": {
                filter: "brightness(1.05)",
                boxShadow: "0 12px 30px rgba(17,24,39,0.65)",
                background:
                  "linear-gradient(90deg, #22d3ee 0%, #6366f1 50%, #3b82f6 100%)",
              },
              "&.Mui-disabled": {
                opacity: 0.6,
                color: "rgba(255,255,255,0.9)",
              },
            }}
          >
            {submitting ? (
              <Box display="flex" gap={1} alignItems="center">
                <CircularProgress size={22} sx={{ color: "white" }} />
                Submitting…
              </Box>
            ) : (
              "Register"
            )}
          </Button>
        </Box>

        <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.12)" }} />

        <Box textAlign="center">
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
            Already have an account?{" "}
            <Link
              to="/login"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              style={{
                color: "#93c5fd",
                fontWeight: 800,
                textDecoration: "underline",
                textUnderlineOffset: "3px",
              }}
            >
              Login
            </Link>
          </Typography>
        </Box>
      </Paper>

      <style>{`
        @keyframes bgmove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* Shared TextField MUI styles */
const fieldSx = {
  "& .MuiInputBase-root": {
    color: "#fff",
    backgroundColor: "rgba(255,255,255,0.06)",
    borderRadius: 2,
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.8)" },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.22)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.35)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.55)",
  },
  "& .MuiFormHelperText-root": {
    color: "rgba(255,255,255,0.75)",
  },
};




















































// import React, { useState } from 'react';
// import { Link } from 'react-router-dom';
// import { TextField, Button, Checkbox, FormControlLabel, Box, Typography, Paper } from '@mui/material';
// import axios from 'axios';

// const RegisterPage = () => {
//   const [formData, setFormData] = useState({
//     name: '',
//     email: '',
//     mobile: '',
//     programName: '',
//   });
//   const [error, setError] = useState('');
//   const [success, setSuccess] = useState('');

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData({ ...formData, [name]: value });
//   };

//   const handleRegister = async (e) => {
//     e.preventDefault();
//     try {
//       const response = await axios.post('http://localhost:5000/register', formData);
//       setSuccess(response.data.message);
//       setError('');
//     } catch (err) {
//       setError(err.response?.data?.message || 'Something went wrong');
//       setSuccess('');
//     }
//   };

//   return (
//     <Box
//       sx={{
//         height: '100vh',
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center',
//         background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
//         padding: 2,
//       }}
//     >
//       <Paper
//         elevation={12}
//         sx={{
//           padding: 4,
//           borderRadius: 10,
//           maxWidth: 400,
//           width: '90%',
//           boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.1)',
//         }}
//       >
//         <Box textAlign="center" mb={3}>
//           <Typography variant="h4" component="h1" fontWeight="bold" color="primary" gutterBottom>
//             Register
//           </Typography>
//           <Typography variant="body2" color="textSecondary">
//             Sign up to unlock the full experience!
//           </Typography>
//         </Box>

//         <form onSubmit={handleRegister}>
//           <TextField
//             label="Full Name"
//             name="name"
//             variant="outlined"
//             fullWidth
//             margin="normal"
//             required
//             value={formData.name}
//             onChange={handleInputChange}
//           />
//           <TextField
//             label="Email"
//             name="email"
//             variant="outlined"
//             fullWidth
//             margin="normal"
//             required
//             value={formData.email}
//             onChange={handleInputChange}
//           />
//           <TextField
//             label="Mobile"
//             name="mobile"
//             variant="outlined"
//             fullWidth
//             margin="normal"
//             required
//             value={formData.mobile}
//             onChange={handleInputChange}
//           />
//           <TextField
//             label="Program Name"
//             name="programName"
//             variant="outlined"
//             fullWidth
//             margin="normal"
//             required
//             value={formData.programName}
//             onChange={handleInputChange}
//           />

//           <FormControlLabel
//             control={<Checkbox color="primary" />}
//             label={
//               <Typography variant="body2" color="textSecondary">
//                 I agree to the{' '}
//                 <Typography component="span" color="primary" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
//                   Terms and Conditions
//                 </Typography>
//               </Typography>
//             }
//             sx={{ mt: 2 }}
//           />

//           {error && (
//             <Typography color="error" variant="body2" sx={{ mt: 2 }}>
//               {error}
//             </Typography>
//           )}
//           {success && (
//             <Typography color="success" variant="body2" sx={{ mt: 2 }}>
//               {success}
//             </Typography>
//           )}

//           <Button
//             type="submit"
//             variant="contained"
//             fullWidth
//             sx={{
//               mt: 3,
//               py: 1.5,
//               fontSize: '1rem',
//               fontWeight: 'bold',
//               textTransform: 'none',
//               background: 'linear-gradient(135deg, #a1c4fd, #c2e9fb)',
//               color: '#fff',
//               ':hover': {
//                 background: 'linear-gradient(135deg, #91b3f4, #aedcf7)',
//               },
//             }}
//           >
//             Register
//           </Button>
//         </form>

//         <Box textAlign="center" mt={3}>
//           <Typography variant="body2">
//             Already have an account?{' '}
//             <Link to="/login" style={{ textDecoration: 'none' }}>
//               <Typography component="span" variant="body2" color="primary" sx={{ fontWeight: 'bold', cursor: 'pointer' }}>
//                 Login
//               </Typography>
//             </Link>
//           </Typography>
//         </Box>
//       </Paper>
//     </Box>
//   );
// };

// export default RegisterPage;
